import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { register } from '../services/api';
import namkeAuthBanner from '../assets/namke-auth-banner.png';
import namkeLogoHeader from '../assets/namke-logo-header.png';
import TurnstileWidget from '../components/TurnstileWidget';

const initialClient = {
  email: '',
  password: '',
  profile_type: 'client',
  first_name: '',
  last_name: '',
  phone: '',
  birth_date: '',
  address: '',
};

const inputClass =
  'w-full min-h-[54px] px-6 bg-namke-cream/60 border border-namke-black/10 rounded-full focus:outline-none focus:ring-2 focus:ring-namke-darkGreen/40 focus:border-transparent transition-colors text-namke-black placeholder-namke-black/40 text-base';

const strengthScore = (pw) => (pw.length >= 12 ? 3 : pw.length >= 8 ? 2 : pw.length > 0 ? 1 : 0);
const strengthColor = (score) => (score === 1 ? '#EF4C23' : score === 2 ? '#FFC717' : '#72B744');
const strengthLabel = ['À compléter', 'Trop court', 'Correct', 'Solide'];

export default function Signup() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [clientData, setClientData] = useState(initialClient);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        email: clientData.email,
        password: clientData.password,
        profile_type: 'client',
        first_name: clientData.first_name,
        last_name: clientData.last_name,
        phone: clientData.phone || undefined,
        birth_date: clientData.birth_date || undefined,
        address: clientData.address || undefined,
        turnstile_token: turnstileToken || undefined,
      };
      await register(payload);
      setRegisteredEmail(clientData.email);
      setRegistered(true);
    } catch (err) {
      const msg = err?.message || '';
      if (/déjà utilisée|already registered|email/i.test(msg)) {
        setError(t('auth.emailAlreadyUsed'));
      } else {
        setError(msg || t('auth.signupError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const score = strengthScore(clientData.password);

  if (registered) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#FCFBF5', padding: '24px' }}>
        <div style={{ width: '100%', maxWidth: '440px', background: '#FCFBF5', borderRadius: '32px', padding: '44px 36px', textAlign: 'center', boxShadow: '0 20px 50px rgba(5,16,4,0.1)' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '999px', background: 'rgba(114,183,68,0.18)', display: 'grid', placeItems: 'center', margin: '0 auto 24px' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#054625" strokeWidth={2} strokeLinecap="round" style={{ width: '30px', height: '30px' }}>
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 style={{ margin: '0 0 12px', fontWeight: 900, fontSize: '26px', letterSpacing: '-0.02em' }}>{t('auth.checkEmail')}</h2>
          <p style={{ margin: '0 0 6px', fontSize: '15px', color: 'rgba(5,16,4,0.7)' }}>{t('auth.confirmationEmailSentTo')}</p>
          <p style={{ margin: '0 0 24px', fontWeight: 600, color: '#054625' }}>{registeredEmail}</p>
          <p style={{ margin: '0 0 28px', fontSize: '13.5px', color: 'rgba(5,16,4,0.5)' }}>{t('auth.clickLinkToActivate')}</p>
          <Link
            to="/login"
            style={{ display: 'inline-block', background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', padding: '14px 32px', borderRadius: '999px' }}
          >
            {t('auth.goToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', background: '#FCFBF5' }}>
      {/* Colonne formulaire */}
      <div style={{ padding: 'clamp(24px, 4vw, 56px) clamp(20px, 5vw, 72px)', display: 'flex', flexDirection: 'column', gap: 'clamp(20px, 2.6vw, 32px)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
          <img src={namkeLogoHeader} alt="namke" style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
        </Link>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '22px', maxWidth: '460px', width: '100%', margin: '0 auto' }}>
          {/* Barre de progression */}
          <div className="flex items-center gap-3.5">
            <span style={{ flex: 1, height: '6px', borderRadius: '999px', background: 'rgba(5,16,4,0.12)' }}>
              <span style={{ display: 'block', height: '6px', width: step === 1 ? '50%' : '100%', borderRadius: '999px', background: '#72B744', transition: 'width 200ms ease' }} />
            </span>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, color: 'rgba(5,16,4,0.62)', whiteSpace: 'nowrap' }}>{step}/2</span>
          </div>

          <div key={`heading-${step}`} className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23' }}>Inscription</span>
            <h1 style={{ margin: 0, fontWeight: 900, fontSize: 'clamp(30px, 3.6vw, 48px)', lineHeight: 1.02, letterSpacing: '-0.025em' }}>
              {step === 1 ? 'Vous venez pour manger ou pour cuisiner ?' : 'Créer votre compte'}
            </h1>
            <p style={{ margin: 0, fontSize: '15.5px', lineHeight: 1.6, color: 'rgba(5,16,4,0.74)' }}>
              {step === 1 ? 'Vous pourrez basculer plus tard depuis vos réglages.' : 'Vos préférences alimentaires seront notées juste après.'}
            </p>
          </div>

          {step === 1 ? (
            <div key="step1" className="animate-fade-in-delay" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <button
                type="button"
                onClick={() => setStep(2)}
                style={{ cursor: 'pointer', display: 'flex', gap: '16px', alignItems: 'flex-start', borderRadius: '28px', padding: '22px', background: '#FBF9D9', border: '2px solid #EF4C23', fontFamily: 'Poppins, sans-serif', textAlign: 'left', transition: 'transform 150ms ease, box-shadow 150ms ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 30px rgba(5,16,4,0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <span style={{ flex: 'none', width: '52px', height: '52px', borderRadius: '999px', display: 'grid', placeItems: 'center', background: '#EF4C23', color: '#FCFBF5' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z"></path><path d="M7 21h10"></path><path d="M12 3v4"></path></svg>
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>Je commande</strong>
                  <span style={{ fontSize: '14px', lineHeight: 1.5, color: 'rgba(5,16,4,0.74)' }}>Parcourir les marmites du quartier, commander, faire livrer.</span>
                </span>
                <span style={{ marginLeft: 'auto', flexShrink: 0, width: '26px', height: '26px', borderRadius: '999px', display: 'grid', placeItems: 'center', fontSize: '13px', fontWeight: 700, background: '#EF4C23', color: '#FCFBF5' }}>✓</span>
              </button>

              <button
                type="button"
                onClick={() => navigate('/business/signup')}
                style={{ cursor: 'pointer', display: 'flex', gap: '16px', alignItems: 'flex-start', borderRadius: '28px', padding: '22px', background: 'transparent', border: '1px solid rgba(5,16,4,0.14)', fontFamily: 'Poppins, sans-serif', textAlign: 'left', transition: 'transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 30px rgba(5,16,4,0.08)'; e.currentTarget.style.background = 'rgba(114,183,68,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ flex: 'none', width: '52px', height: '52px', borderRadius: '999px', display: 'grid', placeItems: 'center', background: '#72B744', color: '#051004' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.46.32-.85.73-1.05a4 4 0 0 0-2.13-7.58 5 5 0 0 0-9.19 0 4 4 0 0 0-2.14 7.58c.42.2.73.59.73 1.05V20a1 1 0 0 0 1 1Z"></path><path d="M6 17h12"></path></svg>
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>Je cuisine</strong>
                  <span style={{ fontSize: '14px', lineHeight: 1.5, color: 'rgba(5,16,4,0.74)' }}>Ouvrir ma boutique, publier mes plats du jour, suivre mes versements.</span>
                </span>
              </button>

              <span style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', background: '#FBF9D9', borderRadius: '22px', padding: '16px 20px' }}>
                <span style={{ flex: 'none', width: '28px', height: '28px', borderRadius: '999px', background: '#FFC717', color: '#051004', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: '14px' }}>i</span>
                <span style={{ fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.78)' }}>Vous pourrez devenir cuisinier plus tard, sans recréer de compte.</span>
              </span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} key="step2" className="animate-fade-in-delay" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {error && <div className="form-message form-message--error text-center">{error}</div>}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '14px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Prénom</span>
                  <input type="text" name="first_name" className={inputClass} placeholder="Awa" value={clientData.first_name} onChange={handleChange} required />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Nom</span>
                  <input type="text" name="last_name" className={inputClass} placeholder="Diallo" value={clientData.last_name} onChange={handleChange} required />
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>E-mail</span>
                <input type="email" name="email" className={inputClass} placeholder="vous@exemple.com" value={clientData.email} onChange={handleChange} required />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Mot de passe</span>
                <span className="flex items-center gap-3" style={{ border: '1px solid rgba(5,16,4,0.1)', background: 'rgba(251,249,217,0.6)', borderRadius: '999px', padding: '0 6px 0 22px', minHeight: '54px' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="8 caractères minimum"
                    value={clientData.password}
                    onChange={handleChange}
                    required
                    style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '16px', color: '#051004', outline: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ border: 'none', cursor: 'pointer', background: '#FCFBF5', color: '#054625', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, minHeight: '38px', padding: '0 16px', borderRadius: '999px', whiteSpace: 'nowrap' }}
                  >
                    {showPassword ? 'Masquer' : 'Afficher'}
                  </button>
                </span>
              </label>

              <div className="flex items-center gap-1.5" style={{ padding: '0 6px' }}>
                {[1, 2, 3].map((i) => (
                  <span key={i} style={{ flex: 1, height: '5px', borderRadius: '999px', background: score >= i ? strengthColor(score) : 'rgba(5,16,4,0.14)' }} />
                ))}
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.66)', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                  {strengthLabel[score]}
                </span>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Ville</span>
                <input type="text" name="address" className={inputClass} placeholder="Berlin" value={clientData.address} onChange={handleChange} />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>Téléphone</span>
                <input type="tel" name="phone" className={inputClass} placeholder={t('auth.phoneOptional')} value={clientData.phone} onChange={handleChange} />
              </label>

              <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />

              <div className="flex gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: '#054625', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15.5px', minHeight: '56px', padding: '0 26px', borderRadius: '999px', whiteSpace: 'nowrap' }}
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={loading || (!!process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken)}
                  style={{ flex: '1 1 200px', border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '56px', borderRadius: '999px', whiteSpace: 'nowrap', opacity: loading ? 0.7 : 1, transition: 'transform 150ms ease' }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px) scale(1.01)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                >
                  {loading ? t('auth.creatingAccount') : 'Créer mon compte'}
                </button>
              </div>
            </form>
          )}

          <span style={{ textAlign: 'center', fontSize: '15px', color: 'rgba(5,16,4,0.74)' }}>
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" style={{ fontWeight: 600 }}>{t('auth.login')}</Link>
          </span>
        </div>

        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.58)' }}>© 2026 namke</span>
      </div>

      {/* Aside */}
      <aside style={{ position: 'relative', background: '#054625', color: '#FBF9D9', minHeight: '420px', padding: 'clamp(24px, 3.5vw, 52px)', display: 'flex', flexDirection: 'column', gap: '26px', justifyContent: 'center', overflow: 'hidden' }} className="hidden lg:flex">
        <span style={{ position: 'absolute', right: '-120px', top: '-140px', width: '360px', height: '360px', borderRadius: '999px', background: '#72B744', opacity: 0.24 }} />
        <div className="animate-fade-in" style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '440px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFC717' }}>Rejoindre namke</span>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 40px)', lineHeight: 1.04, letterSpacing: '-0.025em' }}>
            Trois minutes, et le quartier passe à table
          </h2>
        </div>
        <div className="animate-fade-in-delay" style={{ position: 'relative', width: 'min(320px, 100%)', borderRadius: '220px 220px 34px 220px', overflow: 'hidden' }}>
          <img src={namkeAuthBanner} alt="" style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover' }} />
        </div>
        <ul className="animate-fade-in-delay-2" style={{ position: 'relative', margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '440px' }}>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Inscription gratuite, aucune carte demandée</li>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Vos préférences et allergies notées une seule fois</li>
          <li className="flex gap-3" style={{ fontSize: '15px', lineHeight: 1.5 }}><span style={{ color: '#FFC717', fontWeight: 700 }}>✓</span>Côté cuisinier : commission 12 %, versements chaque lundi</li>
        </ul>
      </aside>
    </div>
  );
}
